/**
 * AI editing-agent chat hook for the editor V2 rewrite.
 *
 * Wraps `useChat` (AI SDK v5) against `/api/editor-agent`: streams the
 * assistant's reply and, for every client-side tool call it emits, applies
 * the edit to the Zustand store immediately (`applyEditingToolCall`) so
 * changes show up live on the timeline ("variant A" — no server round trip
 * needed to render the edit). The tool result is then reported back via
 * `addToolOutput` so the SDK's multi-step loop can continue.
 *
 * ONE exception: `proposeGeneration` (Canny → Supercomputer) is a
 * client-side tool `onToolCall` deliberately does NOT resolve — see the
 * comment on that branch below. `addToolOutput` is exported from this hook
 * so `EditorChatSidebar` can resolve it later, from an Allow/Decline button
 * click, using the same deferred-tool-result mechanism.
 *
 * Verified against the INSTALLED `@ai-sdk/react@2.0.212` / `ai@5.0.210`
 * types (see `node_modules/ai/dist/index.d.ts`):
 *  - `addToolResult` is deprecated in favor of `addToolOutput` — same shape
 *    `{ tool, toolCallId, output, state? }`. We use `addToolOutput`.
 *  - `onToolCall`'s `toolCall` is `ToolCall<string, unknown>` for the
 *    default (untyped) `UIMessage` — `.toolName: string`, `.input: unknown`
 *    (there is no `.args`; the brief's fallback was for an older API).
 *  - `DefaultChatTransport`'s `headers`/`body` are `Resolvable<T>` = `T |
 *    Promise<T> | (() => T) | (() => Promise<T>)`; `HttpChatTransport`
 *    calls `resolve()` on them inside `sendMessages`, i.e. on every send —
 *    so plain functions (not `prepareSendMessagesRequest`) are enough to
 *    get a fresh token + fresh store snapshot per request.
 *  - `sendAutomaticallyWhen: lastAssistantMessageIsCompleteWithToolCalls`
 *    is exported from `ai` and matches `ChatInit`'s
 *    `(options: { messages }) => boolean | PromiseLike<boolean>` exactly
 *    for the default `UIMessage` type — this is how the loop auto-continues
 *    after a client tool result without the user sending another message.
 *  - Per the AI SDK docs, `addToolOutput` must NOT be awaited inside
 *    `onToolCall` (it would deadlock the still-streaming response); it is
 *    called fire-and-forget here.
 */
'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { useChat } from '@ai-sdk/react';
import { DefaultChatTransport, lastAssistantMessageIsCompleteWithToolCalls } from 'ai';
import { useAuthToken } from '@/hooks/useAuthToken';
import { useEditorStore } from '@/lib/editor/editorStore';
import { safeClipDuration } from '@/lib/editor/editorClips';
import { applyEditingToolCall } from '@/lib/editor/agent/applyToolCall';
import { editorHistoryToUIMessages } from '@/lib/editor/agent/history';
import type { ProjectSnapshot } from '@/lib/editor/agent/systemPrompt';
import type { AgentExtras, EditorChatMessage } from '@/types/editor';

function buildSnapshot(): ProjectSnapshot {
  const s = useEditorStore.getState();
  return {
    aspectRatio: s.aspectRatio,
    duration: s.clips.reduce((total, clip) => total + safeClipDuration(clip.duration), 0),
    crossfadeDuration: s.crossfadeDuration,
    trackSettings: s.trackSettings,
    clips: s.clips.map((c) => ({
      id: c.id,
      title: c.title,
      start: c.start,
      duration: safeClipDuration(c.duration),
      playbackRate: c.playbackRate,
      volume: c.volume,
      fit: c.fit,
      filterId: c.filterId,
    })),
    layers: s.layers.map((layer) => ({
      id: layer.id,
      type: layer.type,
      label: layer.label,
      text: layer.text,
      x: layer.x,
      y: layer.y,
      start: layer.start,
      end: layer.end,
      transitionIn: layer.transitionIn
        ? `${layer.transitionIn.type}/${layer.transitionIn.durationSec}с`
        : undefined,
      transitionOut: layer.transitionOut
        ? `${layer.transitionOut.type}/${layer.transitionOut.durationSec}с`
        : undefined,
    })),
    audioTracks: s.audioTracks.map((track) => ({
      id: track.id,
      title: track.fileName,
      start: track.start,
      duration: track.timelineDuration ?? track.duration,
      volume: track.volume,
      ducking: track.ducking,
      muted: track.muted,
    })),
    library: s.library.map((m) => {
      // Upload/studio APIs can contain legacy numeric strings even though the
      // TS contract says `number`. Normalize at the transport boundary so a
      // single old asset cannot make the whole agent request invalid.
      const duration = Number(m.duration);
      return {
        id: m.id,
        videoId: m.videoId,
        title: m.title,
        duration: Number.isFinite(duration) && duration > 0 ? duration : undefined,
        source: m.source,
        contentStatus: m.contentAnalysis?.status,
      };
    }),
  };
}

export interface UseEditorAgentResult {
  messages: ReturnType<typeof useChat>['messages'];
  input: string;
  setInput: (value: string) => void;
  sendMessage: (text: string, extras?: AgentExtras) => void;
  status: ReturnType<typeof useChat>['status'];
  error: ReturnType<typeof useChat>['error'];
  /** Re-submit the current message list (including completed tool outputs). */
  retry: () => void;
  lastToolSummaries: string[];
  /**
   * Resolves a still-pending client-side tool call (currently only
   * `proposeGeneration`, which `onToolCall` below deliberately leaves
   * unresolved) from anywhere — e.g. a button click in `EditorChatSidebar`
   * long after the triggering `onToolCall` invocation returned. Finds the
   * part by `toolCallId` in the current chat state, flips it to
   * `output-available`, and lets `sendAutomaticallyWhen` resume the loop.
   */
  addToolOutput: ReturnType<typeof useChat>['addToolOutput'];
  /** Attachments/mentions of the most recently sent message (for the live bubble). */
  lastSent: AgentExtras | null;
  /** Hydrate the AI SDK message list with the durable project chat log. */
  hydrateHistory: (history: EditorChatMessage[]) => void;
}

export function useEditorAgent(projectId: string): UseEditorAgentResult {
  const { getToken, authedFetch } = useAuthToken();
  const [input, setInput] = useState('');
  const [lastToolSummaries, setLastToolSummaries] = useState<string[]>([]);
  const [lastSent, setLastSent] = useState<AgentExtras | null>(null);
  const sendRef = useRef<ReturnType<typeof useChat>['sendMessage'] | null>(null);
  const appliedToolInTurnRef = useRef(false);
  const automaticRetryCountRef = useRef(0);
  const hydratedHistoryProjectRef = useRef<string | null>(null);
  // Extras (mentions/images/docs) for the NEXT outgoing request only. Set by
  // `sendMessage` right before calling `send(...)`, then read-and-cleared by
  // `body()` below the very first time it runs for that request. `body()` is
  // a plain function re-invoked by HttpChatTransport on every HTTP call it
  // makes — including the tool-loop's automatic continuations driven by
  // `sendAutomaticallyWhen` (those are separate `sendMessages()` calls with
  // no new user turn, so `sendMessage()`/this ref is never touched again in
  // between). Clearing the ref INSIDE `body()` itself (rather than via a
  // `queueMicrotask` after `send(...)`) ties the clear to the actual number
  // of times a request body is built — exactly once per HTTP call — instead
  // of assuming a fixed number of microtask ticks between calling `send()`
  // and the SDK internally invoking `body()`. That timing is an internal
  // implementation detail of `AbstractChat.sendMessage` (it currently awaits
  // `convertFileListToFileUIParts()` before reaching `transport.sendMessages`
  // → `resolve(this.body)` → `body()`), so relying on it would be fragile
  // across `ai` package versions. Net effect: the first `body()` call after
  // a `sendMessage(text, extras)` includes `extras`; every subsequent
  // `body()` call (auto-continue steps of the same tool loop, or a later
  // `sendMessage()` call with no extras) sees an already-empty ref.
  const pendingExtrasRef = useRef<AgentExtras>({});

  const { messages, setMessages, sendMessage: send, status, error, clearError, addToolOutput } = useChat({
    id: `editor-agent-${projectId}`,
    transport: new DefaultChatTransport({
      api: '/api/editor-agent',
      // authedFetch isn't usable here (it awaits + parses JSON; this needs a
      // raw stream), so the bearer token is attached as a header instead.
      // Function form is re-resolved by HttpChatTransport on every send, so
      // the token is always fresh.
      headers: () => ({ Authorization: `Bearer ${getToken() ?? ''}` }),
      // Same re-resolution guarantee for the body: modelId/context are read
      // fresh from the store (via `getState()`, not a reactive selector) on
      // every call, so a `setModelId(...)` made between turns is picked up
      // by the very next send instead of being frozen at mount time.
      // `pendingExtrasRef` is read-and-cleared here (see comment above) so
      // mentions/images/docs are attached to exactly one outgoing request.
      body: () => {
        const extras = pendingExtrasRef.current;
        pendingExtrasRef.current = {};
        return {
          modelId: useEditorStore.getState().modelId,
          projectId,
          context: buildSnapshot(),
          ...extras,
        };
      },
    }),
    sendAutomaticallyWhen: lastAssistantMessageIsCompleteWithToolCalls,
    onError: (chatError) => {
      // A provider can close the stream after emitting a valid client tool
      // call but before sending the final acknowledgement. The edit has
      // already been applied locally at that point. Continue the SAME message
      // list once (it contains the completed tool output) instead of replaying
      // the user's prompt and applying the edit twice.
      console.error('[editor-agent] chat stream failed', {
        projectId,
        message: chatError.message,
        afterClientTool: appliedToolInTurnRef.current,
      });
      if (appliedToolInTurnRef.current && automaticRetryCountRef.current === 0) {
        automaticRetryCountRef.current = 1;
        window.setTimeout(() => {
          void sendRef.current?.();
        }, 350);
      }
    },
    onToolCall: ({ toolCall }) => {
      // `proposeGeneration` is a client-side tool with no server `execute`,
      // same as the editing tools below — but unlike them, it must NOT be
      // resolved here. Its part streams in as `state: 'input-available'`
      // and stays that way; `EditorChatSidebar` renders the Allow/Decline
      // card from it and calls `addToolOutput` only once the user decides,
      // which can be arbitrarily long after this callback returns. Do NOT
      // call `applyEditingToolCall` or `addToolOutput` for it here —
      // `sendAutomaticallyWhen` simply won't fire until that later call
      // resolves the part, so the loop waits with no timeout.
      if (toolCall.toolName === 'proposeGeneration') {
        const input = toolCall.input as {
          summary?: unknown;
          shots?: unknown;
        };
        // Reserve the paid-generation proposal at the exact moment the SDK
        // delivers the tool call. Previously the sidebar tried to recover it
        // later by inspecting rendered UIMessage parts. A fast stream/history
        // hydration could move that part past `input-available` before the
        // effect observed it, leaving only the misleading durable text
        // “Подготовлен план генерации” and no task/card to approve.
        // `createGenProposal` reserves synchronously before its first await and
        // is idempotent by toolCallId, so the sidebar's defensive fallback can
        // safely see the same call without creating or quoting it twice.
        if (typeof input.summary === 'string' && Array.isArray(input.shots) && input.shots.length > 0) {
          void useEditorStore.getState().createGenProposal(
            toolCall.toolCallId,
            input.summary,
            input.shots,
            useEditorStore.getState().lastBriefReferenceUrl,
            authedFetch
          );
        }
        return;
      }
      if (toolCall.toolName === 'searchLibraryContent') return;
      const result = applyEditingToolCall(toolCall.toolName, toolCall.input);
      appliedToolInTurnRef.current = true;
      setLastToolSummaries((prev) => [...prev, result.summary]);
      // Fire-and-forget: awaiting here would deadlock the in-flight stream
      // (the SDK needs to keep reading the response while this resolves).
      addToolOutput({
        tool: toolCall.toolName,
        toolCallId: toolCall.toolCallId,
        output: result,
      });
    },
  });

  // `onError` is created as part of the same useChat initializer, so keep the
  // latest send function in a ref for its delayed continuation callback.
  useEffect(() => {
    sendRef.current = send;
  }, [send]);

  const retry = useCallback(() => {
    automaticRetryCountRef.current = 1;
    clearError();
    void send();
  }, [clearError, send]);

  const hydrateHistory = useCallback((history: EditorChatMessage[]) => {
    if (hydratedHistoryProjectRef.current === projectId) return;
    hydratedHistoryProjectRef.current = projectId;
    const durableMessages = editorHistoryToUIMessages(history);
    setMessages((current) => {
      if (current.length === 0) return durableMessages;
      const liveIds = new Set(current.map((message) => message.id));
      return [...durableMessages.filter((message) => !liveIds.has(message.id)), ...current];
    });
  }, [projectId, setMessages]);

  const sendMessage = useCallback(
    (text: string, extras?: AgentExtras) => {
      const trimmed = text.trim();
      const hasExtras = !!(
        extras &&
        ((extras.mentions?.length ?? 0) > 0 ||
          (extras.imageUrls?.length ?? 0) > 0 ||
          (extras.docTexts?.length ?? 0) > 0)
      );
      if (!trimmed && !hasExtras) return;
      appliedToolInTurnRef.current = false;
      automaticRetryCountRef.current = 0;
      // Stash extras for the very next `body()` call (the one that builds
      // this request) — see `pendingExtrasRef` comment above for why the
      // read-and-clear happens inside `body()` rather than here.
      pendingExtrasRef.current = extras ?? {};
      // Remember this turn's reference image (if any) for a later
      // `proposeGeneration` tool call to pick up — the composer clears its
      // own attachment state right after send, so the store is the only
      // place this survives. Overwritten (including to `null`) on every
      // send, not just when an image is attached, so a proposal always
      // reflects the MOST RECENT message's reference, not a stale one.
      useEditorStore.getState().setLastBriefReferenceUrl(extras?.imageUrls?.[0] ?? null);
      // Remember this turn's attachments so the sidebar can show them under the
      // just-sent user bubble (live useChat messages don't carry them; history
      // persists them separately). Reset to null on a plain text send.
      setLastSent(hasExtras ? (extras ?? null) : null);
      void send({ text: trimmed || '(см. вложения)' });
      setInput('');
    },
    [send]
  );

  return {
    messages,
    input,
    setInput,
    sendMessage,
    status,
    error,
    retry,
    lastToolSummaries,
    addToolOutput,
    lastSent,
    hydrateHistory,
  };
}
